import { useEffect, useRef, useState } from 'react';
import { ApiError, api } from '../lib/api';

/**
 * M29 T5: Dashboard AI support widget.
 *
 * Floating "?" button (bottom-right) opening a docs-grounded chat panel.
 * Mounted inside DashboardLayout only — it never appears on /login or
 * /onboarding, matching the server-side gates (session + viewDashboard +
 * entitlement + rate limit) on POST /api/ai/support-chat.
 *
 * Conversation state is component-local (v1 — refresh clears it; persistence
 * deliberately out of scope, see M29_PLAN.md). Error states map the
 * endpoint's status codes to friendly copy; pending disables the input.
 *
 * Reference: M29_PLAN.md T5, src/handlers/supportChat.ts
 */

interface ChatBubble {
  role: 'user' | 'assistant';
  content: string;
}

/** Mirror of the server's per-message cap (chars). */
const MAX_MESSAGE_CHARS = 2000;
/** Conversation window the server keeps — trim the UI transcript to match. */
const MAX_HISTORY = 7;

export function SupportChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatBubble[]>([
    {
      role: 'assistant',
      content:
        'Hi! I answer questions from the Stripe Sync docs — setup, features, billing, troubleshooting. What can I help with?',
    },
  ]);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Keep the latest message in view as the transcript grows.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, open]);

  async function send() {
    const question = input.trim();
    if (!question || pending) return;
    if (question.length > MAX_MESSAGE_CHARS) {
      setError(`Please keep questions under ${MAX_MESSAGE_CHARS} characters.`);
      return;
    }

    // Window the transcript the same way the server does (last 7, ending on
    // the new user message) so what we send is what the model sees.
    const next = [...messages, { role: 'user' as const, content: question }];
    const windowed = next
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .slice(-MAX_HISTORY);

    setMessages(next);
    setInput('');
    setError(null);
    setPending(true);

    try {
      const res = await api.post<{ answer: string; }>('ai/support-chat', {
        messages: windowed,
      });
      setMessages((prev) => [...prev, { role: 'assistant', content: res.answer }]);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setPending(false);
    }
  }

  return (
    <>
      {/* Floating trigger */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close help chat' : 'Open help chat'}
        title="Help"
        className={[
          'fixed bottom-6 right-6 z-40 flex h-11 w-11 items-center justify-center',
          'rounded-full bg-brand-600 text-lg font-semibold text-white shadow-lg',
          'transition-colors hover:bg-brand-700',
        ].join(' ')}
      >
        {open ? '×' : '?'}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          className={[
            'fixed bottom-20 right-6 z-40 flex h-[28rem] w-[22rem] max-w-[calc(100vw-3rem)]',
            'flex-col overflow-hidden rounded-xl border border-ink-200 bg-white shadow-xl',
          ].join(' ')}
          role="dialog"
          aria-label="Support chat"
        >
          <div className="flex items-center justify-between border-b border-ink-200 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-ink-800">Help</p>
              <p className="text-[11px] text-ink-400">Answers from the Stripe Sync docs</p>
            </div>
            <a
              href="https://hss-docs.ampdeck.io"
              target="_blank"
              rel="noreferrer"
              className="text-[11px] font-medium text-ink-400 transition-colors hover:text-ink-600"
            >
              All docs ↗
            </a>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
            {messages.map((m, i) => (
              <div
                key={i}
                className={[
                  'max-w-[85%] rounded-lg px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap',
                  m.role === 'user'
                    ? 'ml-auto bg-brand-600 text-white'
                    : 'bg-ink-100 text-ink-800',
                ].join(' ')}
              >
                {m.content}
              </div>
            ))}
            {pending && (
              <div className="max-w-[85%] rounded-lg bg-ink-100 px-3 py-2 text-sm text-ink-400">
                Thinking…
              </div>
            )}
            {error && (
              <div className="rounded-lg border border-warn-200 bg-warn-50 px-3 py-2 text-xs leading-relaxed text-warn-700">
                {error}
              </div>
            )}
          </div>

          <div className="border-t border-ink-200 p-3">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  // Enter sends; Shift+Enter adds a newline.
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void send();
                  }
                }}
                rows={2}
                maxLength={MAX_MESSAGE_CHARS}
                placeholder="Ask about setup, billing, features…"
                disabled={pending}
                className="flex-1 resize-none rounded-lg border border-ink-200 px-3 py-2 text-sm text-ink-800 placeholder:text-ink-300 focus:border-brand-500 focus:outline-none disabled:bg-ink-50"
              />
              <button
                type="button"
                onClick={() => void send()}
                disabled={pending || !input.trim()}
                className="rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700 disabled:opacity-40"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/** Map endpoint errors to friendly copy — never surface raw error strings. */
function friendlyError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 402) {
      return 'Support chat is part of the paid plan. You can resubscribe from Settings → Billing.';
    }
    if (err.status === 429) {
      return 'You’ve sent a lot of messages this hour — please wait a bit. Meanwhile the docs are always open: https://hss-docs.ampdeck.io';
    }
    if (err.status === 403) {
      return 'You don’t have permission to use support chat. Ask an admin for the Operator or Admin role.';
    }
    if (err.status === 401) {
      return 'Your session expired — please log in again.';
    }
  }
  return 'Something went wrong reaching the assistant. Please try again in a moment.';
}
